import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import {
  Shield, Lock, Smartphone, Eye, EyeOff, CheckCircle, XCircle,
  Loader2, AlertTriangle, Building2, Calendar, Download, FileText,
  Globe, Server, Bug, Activity, Target, Clock, Users, FileCheck,
  ChevronDown, ChevronRight, ExternalLink, Wifi, Database, Brain,
  Lightbulb, AlertOctagon, TrendingUp, Zap, Map, Route, Network,
  PieChart, BarChart3, FileBarChart, Crosshair, Terminal, Phone,
  HelpCircle, BookOpen, ArrowRight, CheckCircle2, AlertCircle,
  ShieldAlert, ShieldCheck, ShieldX, Info, Sparkles, FileDown,
  MessageCircle, Mail, MapPin, DollarSign, TrendingDown, AlertOctagonIcon,
  Skull, Banknote, Percent, ShieldOff, Lock as LockIcon, Unlock,
  Scale, Flag, X
} from 'lucide-react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { COUNTRY_RISK_DATA, getCountryData, getThreatLevelColor, getThreatLevelLabel, GDPR_STATUS } from '../../data/countryRiskData';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';

const API_URL = process.env.REACT_APP_API_URL || '';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// ==========================================
// COMPOSANTS HERO & METRIQUES
// ==========================================

// Composant Jauge Circulaire Animee
const CircularGauge = ({ value, maxValue = 100, size = 180, strokeWidth = 12, color, label, icon: Icon }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = Math.min(value / maxValue, 1);
  const offset = circumference - (progress * circumference);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
          style={{ filter: `drop-shadow(0 0 10px ${color})` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {Icon && <Icon className="w-6 h-6 mb-1" style={{ color }} />}
        <span className="text-4xl font-bold text-white">{value}</span>
        <span className="text-xs text-gray-400 uppercase tracking-wider">{label}</span>
      </div>
    </div>
  );
};

// Carte Metrique avec Icone
const MetricCard = ({ icon: Icon, label, value, subValue, color, trend, trendDirection }) => {
  return (
    <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/40 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-5 hover:border-gray-600/50 transition-all hover:transform hover:scale-[1.02]">
      <div className="flex items-start justify-between mb-3">
        <div className={`p-3 rounded-xl`} style={{ backgroundColor: `${color}20` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
            trendDirection === 'up' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
          }`}>
            {trendDirection === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {trend}
          </div>
        )}
      </div>
      <div className="text-2xl font-bold text-white mb-1">{value}</div>
      <div className="text-sm text-gray-400">{label}</div>
      {subValue && <div className="text-xs text-gray-500 mt-1">{subValue}</div>}
    </div>
  );
};

// Hero Header Redesigne
const HeroHeader = ({ workspace, stats, vulnerabilities, score }) => {
  // Calculs des metriques
  const criticalCount = stats?.critical_count || 0;
  const highCount = stats?.high_count || 0;
  const mediumCount = stats?.medium_count || 0;
  const totalVulns = vulnerabilities?.length || 0;

  // Calcul probabilite de piratage (basee sur les vulns)
  const hackProbability = Math.min(95, Math.round(
    (criticalCount * 25 + highCount * 15 + mediumCount * 5) + 10
  ));

  // Estimation cout piratage (basee sur taille entreprise et vulns)
  const estimatedCost = Math.round(
    (50000 + criticalCount * 100000 + highCount * 30000 + mediumCount * 5000)
  );

  // Estimation leaks potentiels
  const potentialLeaks = criticalCount * 50000 + highCount * 10000 + mediumCount * 1000;

  // Cyberscore (inverse du score de risque)
  const cyberScore = score || 50;

  const getScoreColor = (s) => {
    if (s >= 80) return '#10B981';
    if (s >= 60) return '#F59E0B';
    if (s >= 40) return '#F97316';
    return '#EF4444';
  };

  const scoreColor = getScoreColor(cyberScore);

  // Logo client
  const logoUrl = workspace?.logo_url || workspace?.logo_base64;

  return (
    <div className="relative overflow-hidden rounded-3xl mb-8">
      {/* Background avec gradient anime */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDIwMjAiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyek0zNCAyNGgtMnYtNGgydjR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 p-8">
        {/* En-tete entreprise */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            {/* Logo Client ou Icone par defaut */}
            {logoUrl ? (
              <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-2 shadow-lg flex items-center justify-center overflow-hidden">
                <img
                  src={logoUrl.startsWith('data:') ? logoUrl : logoUrl}
                  alt="Logo"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-10 h-10 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg></div>';
                  }}
                />
              </div>
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <Building2 className="w-10 h-10 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-white">{workspace?.company_name || workspace?.name || 'Votre Entreprise'}</h1>
              <p className="text-gray-400 flex items-center gap-2 mt-1">
                <Calendar className="w-4 h-4" />
                Audit du {new Date(workspace?.created_at || Date.now()).toLocaleDateString('fr-FR', {
                  day: 'numeric', month: 'long', year: 'numeric'
                })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-sm text-gray-300">Rapport Securise</span>
            <Lock className="w-4 h-4 text-gray-400" />
          </div>
        </div>

        {/* Section Scores Principaux */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* CyberScore - Grande jauge */}
          <div className="lg:col-span-4 flex flex-col items-center justify-center p-6 bg-black/30 backdrop-blur-sm rounded-2xl border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-400" />
              CyberScore
            </h3>
            <CircularGauge
              value={cyberScore}
              maxValue={100}
              size={180}
              strokeWidth={14}
              color={scoreColor}
              label="/100"
              icon={ShieldCheck}
            />
            <div className="mt-4 text-center">
              <span className={`px-4 py-1.5 rounded-full text-sm font-medium`}
                style={{ backgroundColor: `${scoreColor}20`, color: scoreColor }}>
                {cyberScore >= 80 ? 'Securise' : cyberScore >= 60 ? 'Attention Requise' : cyberScore >= 40 ? 'Risque Eleve' : 'Critique'}
              </span>
            </div>
          </div>

          {/* Metriques Cles */}
          <div className="lg:col-span-8 grid grid-cols-2 gap-4">
            {/* Probabilite de Piratage */}
            <MetricCard
              icon={Percent}
              label="Probabilite de Piratage"
              value={`${hackProbability}%`}
              subValue="dans les 12 prochains mois"
              color="#EF4444"
              trend={hackProbability > 50 ? 'Elevee' : 'Moderee'}
              trendDirection={hackProbability > 50 ? 'up' : 'down'}
            />

            {/* Estimated Cost */}
            <MetricCard
              icon={Banknote}
              label="Estimated Cost if Hacked"
              value={`${(estimatedCost / 1000).toFixed(0)}k€`}
              subValue="pertes directes et indirectes"
              color="#F59E0B"
            />

            {/* Data at Risk */}
            <MetricCard
              icon={Database}
              label="Potentially Exposed Data"
              value={potentialLeaks > 1000000 ? `${(potentialLeaks / 1000000).toFixed(1)}M` : `${(potentialLeaks / 1000).toFixed(0)}k`}
              subValue="enregistrements sensibles"
              color="#8B5CF6"
            />

            {/* Vulnerabilities */}
            <MetricCard
              icon={Bug}
              label="Detected Vulnerabilities"
              value={totalVulns}
              subValue={`${criticalCount} critical, ${highCount} high`}
              color="#06B6D4"
            />
          </div>
        </div>

        {/* Barre de statut des vulns */}
        <div className="mt-8 p-4 bg-black/30 backdrop-blur-sm rounded-xl border border-white/10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-400">Vulnerability breakdown</span>
            <span className="text-sm text-gray-500">{totalVulns} total</span>
          </div>
          <div className="flex h-3 rounded-full overflow-hidden bg-gray-800">
            {criticalCount > 0 && (
              <div
                className="bg-red-500 transition-all duration-500"
                style={{ width: `${(criticalCount / Math.max(totalVulns, 1)) * 100}%` }}
              />
            )}
            {highCount > 0 && (
              <div
                className="bg-orange-500 transition-all duration-500"
                style={{ width: `${(highCount / Math.max(totalVulns, 1)) * 100}%` }}
              />
            )}
            {mediumCount > 0 && (
              <div
                className="bg-yellow-500 transition-all duration-500"
                style={{ width: `${(mediumCount / Math.max(totalVulns, 1)) * 100}%` }}
              />
            )}
            {(totalVulns - criticalCount - highCount - mediumCount) > 0 && (
              <div
                className="bg-blue-500 transition-all duration-500"
                style={{ width: `${((totalVulns - criticalCount - highCount - mediumCount) / Math.max(totalVulns, 1)) * 100}%` }}
              />
            )}
          </div>
          <div className="flex items-center justify-center gap-6 mt-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-xs text-gray-400">Critiques ({criticalCount})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500" />
              <span className="text-xs text-gray-400">Elevees ({highCount})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-xs text-gray-400">Moyennes ({mediumCount})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-xs text-gray-400">Faibles ({totalVulns - criticalCount - highCount - mediumCount})</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// COMPOSANT TOOLTIP IP AVEC DESCRIPTION IA
// ==========================================

// Fonction pour generer une description IA de l'origine de l'IP
const generateIPDescription = (location) => {
  const { ip, isp, organization, is_datacenter, is_hosting, is_cdn, as_name, service_type } = location;

  // If we have a predefined description, use it
  if (location.ai_description) {
    return location.ai_description;
  }

  // Generer une description basee sur les metadonnees
  if (is_cdn) {
    return `Cette IP appartient au CDN ${organization || isp || 'inconnu'}, utilise pour accelerer la distribution de votre contenu web.`;
  }
  if (is_hosting && organization) {
    return `Cette IP est hebergee chez ${organization}, probablement un serveur web ou service cloud de votre infrastructure.`;
  }
  if (is_datacenter && isp) {
    return `Cette IP provient du datacenter ${isp}, utilisee pour vos services heberges.`;
  }
  if (service_type === 'dns') {
    return `Cette IP est liee a un serveur DNS ${organization || isp || ''} qui resout les noms de domaine de votre infrastructure.`;
  }
  if (service_type === 'mail') {
    return `Cette IP correspond au serveur de messagerie ${organization || isp || ''} de votre domaine.`;
  }
  if (service_type === 'website') {
    return `Cette IP heberge votre site web principal via ${organization || isp || 'le fournisseur detecte'}.`;
  }
  if (isp || organization) {
    return `Cette IP est geree par ${organization || isp} et fait partie de votre surface d'attaque detectee.`;
  }

  return `Cette IP fait partie de votre infrastructure et a ete detectee lors de l'audit de securite.`;
};

// Composant Tooltip IP avec description IA - utilise Portal pour eviter le clipping
const IPTooltip = ({ ip, location, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const description = useMemo(() => generateIPDescription(location), [location]);

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 8
    });
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const tooltipContent = isVisible ? (
    <div
      className="fixed pointer-events-none"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -100%)',
        zIndex: 99999
      }}
    >
      <div className="bg-gray-900 border-2 border-cyan-500 rounded-lg shadow-2xl p-3 max-w-xs mb-2">
        <div className="flex items-start gap-2">
          <Brain className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-xs font-semibold text-cyan-400 mb-1 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Analyse IA
            </div>
            <p className="text-xs text-gray-300 leading-relaxed">{description}</p>
            {location.isp && (
              <div className="mt-2 pt-2 border-t border-gray-700">
                <span className="text-xs text-gray-500">Provider: </span>
                <span className="text-xs text-gray-400">{location.isp}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Fleche */}
      <div className="flex justify-center -mt-2">
        <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-cyan-500"></div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div
        className="inline-block"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {tooltipContent && ReactDOM.createPortal(tooltipContent, document.body)}
    </>
  );
};

// ==========================================
// COMPOSANT CARTOGRAPHIE INTEGREE
// ==========================================

// Controleur de carte pour ajuster les bounds
function MapController({ locations }) {
  const map = useMap();

  useEffect(() => {
    // Invalider la taille apres un court delai
    const timer = setTimeout(() => {
      map.invalidateSize();

      if (locations && locations.length > 0) {
        const validLocations = locations.filter(l => l.latitude && l.longitude);
        if (validLocations.length > 0) {
          const bounds = L.latLngBounds(
            validLocations.map(l => [l.latitude, l.longitude])
          );
          map.fitBounds(bounds, { padding: [50, 50], maxZoom: 6 });
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [map, locations]);

  return null;
}

// Composant Cartographie Integree (sans auth)
const InlineCartography = ({ workspaceId, hosts = [] }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Generer des donnees de demo basees sur les hosts avec infos ISP
  useEffect(() => {
    const generateLocations = () => {
      // Si on a des hosts, essayer de les geolocaliser
      if (hosts.length > 0) {
        // Simulated data based on hosts with ISP
        const demoLocations = [
          { ip: '195.154.123.45', latitude: 48.8566, longitude: 2.3522, country: 'France', country_code: 'FR', city: 'Paris', threat_level: 'low', isp: 'Scaleway', service_type: 'website' },
          { ip: '78.46.89.12', latitude: 50.1109, longitude: 8.6821, country: 'Allemagne', country_code: 'DE', city: 'Frankfurt', threat_level: 'low', isp: 'Hetzner', is_datacenter: true },
          { ip: '104.26.5.78', latitude: 37.7749, longitude: -122.4194, country: 'Etats-Unis', country_code: 'US', city: 'San Francisco', threat_level: 'medium', isp: 'Cloudflare', is_cdn: true },
          { ip: '91.243.45.67', latitude: 55.7558, longitude: 37.6173, country: 'Russie', country_code: 'RU', city: 'Moscou', threat_level: 'critical', isp: 'Baxet' },
        ];
        setLocations(demoLocations);
      } else {
        // Default data
        setLocations([
          { ip: '192.168.1.1', latitude: 48.8566, longitude: 2.3522, country: 'France', country_code: 'FR', city: 'Paris', threat_level: 'low', isp: 'Local Network' },
          { ip: '10.0.0.1', latitude: 51.5074, longitude: -0.1278, country: 'Royaume-Uni', country_code: 'GB', city: 'Londres', threat_level: 'low', isp: 'Local Network' },
        ]);
      }
      setLoading(false);
    };

    generateLocations();
  }, [hosts]);

  const getThreatColor = (level) => {
    switch (level) {
      case 'critical': return '#EF4444';
      case 'high': return '#F97316';
      case 'medium': return '#F59E0B';
      default: return '#10B981';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-900">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <MapContainer
      center={[48.8566, 2.3522]}
      zoom={4}
      style={{ height: '100%', width: '100%', background: '#1a1a2e' }}
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
      />
      <MapController locations={locations} />

      {locations.map((loc, idx) => (
        <CircleMarker
          key={idx}
          center={[loc.latitude, loc.longitude]}
          radius={12}
          pathOptions={{
            color: getThreatColor(loc.threat_level),
            fillColor: getThreatColor(loc.threat_level),
            fillOpacity: 0.7,
            weight: 2
          }}
        >
          <Popup>
            <div className="text-sm max-w-xs">
              <div className="font-bold">{loc.ip}</div>
              <div>{loc.city}, {loc.country}</div>
              <div className="mt-1" style={{ color: getThreatColor(loc.threat_level) }}>
                Risque: {loc.threat_level === 'critical' ? 'Critique' : loc.threat_level === 'high' ? 'Eleve' : loc.threat_level === 'medium' ? 'Moyen' : 'Faible'}
              </div>
              {/* Description IA */}
              <div className="mt-2 p-2 bg-cyan-50 rounded border border-cyan-200">
                <div className="flex items-center gap-1 text-cyan-700 text-xs font-semibold mb-1">
                  <span>🤖</span> Analyse IA
                </div>
                <p className="text-xs text-gray-700">{generateIPDescription(loc)}</p>
              </div>
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </MapContainer>
  );
};

// Panel d'information pays
const CountryInfoPanel = ({ countryCode, onClose }) => {
  const country = getCountryData(countryCode);
  if (!country) return null;

  return (
    <div className="bg-gray-900/95 backdrop-blur-sm border border-cyan-500/30 rounded-xl shadow-2xl p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-700 pb-3">
        <div className="flex items-center gap-3">
          <Flag className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-bold text-white">{country.name}</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-700 transition-colors"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      {/* Threat Level */}
      <div className="p-3 rounded-lg" style={{ backgroundColor: `${getThreatLevelColor(country.threatLevel)}20` }}>
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4" style={{ color: getThreatLevelColor(country.threatLevel) }} />
          <span className="text-sm font-semibold text-white">Niveau de menace</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getThreatLevelColor(country.threatLevel) }} />
          <span className="text-sm font-medium" style={{ color: getThreatLevelColor(country.threatLevel) }}>
            {getThreatLevelLabel(country.threatLevel)}
          </span>
        </div>
      </div>

      {/* GDPR Status */}
      <div className="p-3 rounded-lg" style={{ backgroundColor: `${country.gdpr?.color || '#6B7280'}20` }}>
        <div className="flex items-center gap-2 mb-2">
          <Scale className="w-4 h-4" style={{ color: country.gdpr?.color || '#6B7280' }} />
          <span className="text-sm font-semibold text-white">Protection des donnees (RGPD)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: country.gdpr?.color || '#6B7280' }} />
          <span className="text-sm" style={{ color: country.gdpr?.color || '#6B7280' }}>
            {country.gdpr?.description || 'Non evalue'}
          </span>
        </div>
        {country.dataProtectionAuthority && (
          <p className="mt-2 text-xs text-gray-400">Autorite: {country.dataProtectionAuthority}</p>
        )}
      </div>

      {/* Hacker Groups */}
      {country.hackerGroups && country.hackerGroups.length > 0 && (
        <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-red-400" />
            <span className="text-sm font-semibold text-white">
              Groupes de hackers connus ({country.hackerGroups.length})
            </span>
          </div>
          <div className="space-y-2">
            {country.hackerGroups.map((group, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-red-300 bg-red-500/10 px-2 py-1 rounded">
                <Target className="w-3 h-3 flex-shrink-0" />
                <span>{group}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cyber Strategy */}
      {country.cyberStrategy && (
        <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-semibold text-white">Strategie cyber nationale</span>
          </div>
          <p className="text-sm text-blue-300">{country.cyberStrategy}</p>
        </div>
      )}

      {/* Notes */}
      {country.notes && (
        <div className="p-3 bg-gray-700/30 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Info className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-semibold text-white">Notes</span>
          </div>
          <p className="text-sm text-gray-300">{country.notes}</p>
        </div>
      )}
    </div>
  );
};

// Section Cartographie avec design ameliore - 100% largeur et infos pays
const CartographySection = ({ workspaceId, hosts }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const [locations, setLocations] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Generer des donnees de demo avec codes pays et infos ISP pour tooltips IA
  useEffect(() => {
    if (isOpen) {
      const demoLocations = [
        {
          ip: '195.154.123.45', latitude: 48.8566, longitude: 2.3522,
          country: 'France', country_code: 'FR', city: 'Paris', threat_level: 'low',
          isp: 'Online SAS (Scaleway)', organization: 'Scaleway', is_hosting: true, is_datacenter: true,
          service_type: 'website',
          ai_description: 'Cette IP appartient a Scaleway (anciennement Online SAS), hebergeant votre serveur web principal en France.'
        },
        {
          ip: '78.46.89.12', latitude: 50.1109, longitude: 8.6821,
          country: 'Allemagne', country_code: 'DE', city: 'Frankfurt', threat_level: 'low',
          isp: 'Hetzner Online GmbH', organization: 'Hetzner', is_hosting: true, is_datacenter: true,
          service_type: 'database',
          ai_description: 'Cette IP est hebergee chez Hetzner en Allemagne, probablement un serveur de base de donnees ou backup de votre infrastructure.'
        },
        {
          ip: '104.26.5.78', latitude: 37.7749, longitude: -122.4194,
          country: 'Etats-Unis', country_code: 'US', city: 'San Francisco', threat_level: 'medium',
          isp: 'Cloudflare Inc', organization: 'Cloudflare', is_cdn: true, is_datacenter: true,
          service_type: 'cdn',
          ai_description: 'Cette IP appartient au CDN Cloudflare qui protege et accelere votre site web. Risque moyen car expose publiquement.'
        },
        {
          ip: '91.243.45.67', latitude: 55.7558, longitude: 37.6173,
          country: 'Russie', country_code: 'RU', city: 'Moscou', threat_level: 'critical',
          isp: 'LLC Baxet', organization: 'Baxet', is_hosting: true,
          ai_description: 'Cette IP russe detectee pourrait indiquer une connexion suspecte ou un service tiers. Verification recommandee.'
        },
        {
          ip: '185.199.108.153', latitude: 51.5074, longitude: -0.1278,
          country: 'Royaume-Uni', country_code: 'GB', city: 'London', threat_level: 'low',
          isp: 'GitHub Inc', organization: 'GitHub Pages', is_cdn: true,
          service_type: 'website',
          ai_description: 'Cette IP appartient a GitHub Pages, utilisee pour heberger de la documentation ou un site statique lie a votre projet.'
        },
        {
          ip: '203.0.113.50', latitude: 39.9042, longitude: 116.4074,
          country: 'Chine', country_code: 'CN', city: 'Beijing', threat_level: 'critical',
          isp: 'China Telecom', organization: 'China Telecom',
          ai_description: 'Cette IP chinoise represente un risque eleve. Verifiez si cette connexion est legitime ou si elle indique une compromission.'
        },
      ];
      setLocations(demoLocations);
    }
  }, [isOpen, hosts]);

  const handleToggle = () => {
    if (!isOpen) {
      setMapKey(prev => prev + 1);
    }
    setIsOpen(!isOpen);
  };

  const getThreatColor = (level) => {
    switch (level) {
      case 'critical': return '#EF4444';
      case 'high': return '#F97316';
      case 'medium': return '#F59E0B';
      default: return '#10B981';
    }
  };

  // Obtenir les pays uniques avec leurs infos et locations completes
  const uniqueCountries = useMemo(() => {
    const countries = {};
    locations.forEach(loc => {
      if (loc.country_code && !countries[loc.country_code]) {
        const countryData = getCountryData(loc.country_code);
        countries[loc.country_code] = {
          code: loc.country_code,
          name: loc.country,
          data: countryData,
          ips: [],
          locations: [] // Stocker les locations completes pour les tooltips
        };
      }
      if (loc.country_code && countries[loc.country_code]) {
        countries[loc.country_code].ips.push(loc.ip);
        countries[loc.country_code].locations.push(loc); // Ajouter la location complete
      }
    });
    return Object.values(countries);
  }, [locations]);

  return (
    <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/40 backdrop-blur-sm border border-gray-700/50 rounded-2xl overflow-hidden">
      {/* Header de la section */}
      <button
        onClick={handleToggle}
        className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-cyan-500/20">
            <Globe className="w-6 h-6 text-cyan-400" />
          </div>
          <div className="text-left">
            <h3 className="text-xl font-bold text-white">Cartographie des Risques et Lois</h3>
            <p className="text-sm text-gray-400">Visualisation geographique des menaces, groupes de hackers et conformite RGPD</p>
          </div>
        </div>
        <ChevronDown className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Contenu de la carte */}
      {isOpen && (
        <div className="border-t border-gray-700/50">
          <div className="p-6">
            {/* Stats rapides */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-black/30 rounded-xl p-4 border border-gray-700/50 text-center">
                <Globe className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">{locations.length}</div>
                <div className="text-xs text-gray-400">IPs Localisees</div>
              </div>
              <div className="bg-black/30 rounded-xl p-4 border border-cyan-500/30 text-center">
                <Flag className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-cyan-400">{uniqueCountries.length}</div>
                <div className="text-xs text-gray-400">Pays</div>
              </div>
              <div className="bg-black/30 rounded-xl p-4 border border-red-500/30 text-center">
                <AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-red-400">{locations.filter(l => l.threat_level === 'critical').length}</div>
                <div className="text-xs text-gray-400">Critiques</div>
              </div>
              <div className="bg-black/30 rounded-xl p-4 border border-yellow-500/30 text-center">
                <Shield className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-yellow-400">{locations.filter(l => l.threat_level === 'medium').length}</div>
                <div className="text-xs text-gray-400">Moyens</div>
              </div>
              <div className="bg-black/30 rounded-xl p-4 border border-green-500/30 text-center">
                <ShieldCheck className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-green-400">{locations.filter(l => l.threat_level === 'low').length}</div>
                <div className="text-xs text-gray-400">Faibles</div>
              </div>
            </div>

            {/* Legende */}
            <div className="flex flex-wrap gap-4 mb-4 justify-center">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs text-red-400 font-medium">Risque Critique</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <span className="text-xs text-orange-400 font-medium">Risque Eleve</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="text-xs text-yellow-400 font-medium">Risque Moyen</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-xs text-green-400 font-medium">Risque Faible</span>
              </div>
            </div>

            {/* Carte - 100% largeur */}
            <div style={{ height: '700px' }} className="rounded-xl overflow-hidden border-2 border-cyan-500/30 bg-[#1a1a2e] relative">
              <MapContainer
                key={mapKey}
                center={[48.8566, 2.3522]}
                zoom={3}
                style={{ height: '100%', width: '100%', background: '#1a1a2e' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                />
                <MapController locations={locations} />

                {locations.map((loc, idx) => (
                  <CircleMarker
                    key={idx}
                    center={[loc.latitude, loc.longitude]}
                    radius={loc.threat_level === 'critical' ? 18 : 14}
                    pathOptions={{
                      color: getThreatColor(loc.threat_level),
                      fillColor: getThreatColor(loc.threat_level),
                      fillOpacity: 0.7,
                      weight: 3
                    }}
                    eventHandlers={{
                      click: () => setSelectedCountry(loc.country_code)
                    }}
                  >
                    <Popup>
                      <div className="text-sm p-1 max-w-xs">
                        <div className="font-bold text-gray-900">{loc.ip}</div>
                        <div className="text-gray-600">{loc.city}, {loc.country}</div>
                        <div className="mt-1 font-medium" style={{ color: getThreatColor(loc.threat_level) }}>
                          Risque: {loc.threat_level === 'critical' ? 'Critique' : loc.threat_level === 'high' ? 'Eleve' : loc.threat_level === 'medium' ? 'Moyen' : 'Faible'}
                        </div>
                        {/* Description IA de l'IP */}
                        <div className="mt-2 p-2 bg-cyan-50 rounded border border-cyan-200">
                          <div className="flex items-center gap-1 text-cyan-700 text-xs font-semibold mb-1">
                            <span>🤖</span> Analyse IA
                          </div>
                          <p className="text-xs text-gray-700 leading-relaxed">
                            {generateIPDescription(loc)}
                          </p>
                          {loc.isp && (
                            <div className="mt-1 text-xs text-gray-500">
                              Provider: {loc.isp}
                            </div>
                          )}
                        </div>
                        <button
                          className="mt-2 text-xs text-cyan-600 hover:text-cyan-500 underline"
                          onClick={() => setSelectedCountry(loc.country_code)}
                        >
                          View country details
                        </button>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>

              {/* Panel d'info pays */}
              {selectedCountry && (
                <div className="absolute top-4 right-4 w-96 max-h-[calc(100%-2rem)] overflow-y-auto z-[1000]">
                  <CountryInfoPanel
                    countryCode={selectedCountry}
                    onClose={() => setSelectedCountry(null)}
                  />
                </div>
              )}
            </div>

            {/* Pays detectes avec infos */}
            <div className="mt-6">
              <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Flag className="w-5 h-5 text-cyan-400" />
                Pays detectes et analyse des risques
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {uniqueCountries.map((country, idx) => {
                  const countryData = country.data;
                  const threatColor = countryData ? getThreatLevelColor(countryData.threatLevel) : '#6B7280';

                  return (
                    <div
                      key={idx}
                      className="bg-black/30 rounded-xl p-4 border border-gray-700/50 hover:border-cyan-500/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedCountry(country.code)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${threatColor}20` }}>
                            <Flag className="w-5 h-5" style={{ color: threatColor }} />
                          </div>
                          <div>
                            <div className="text-white font-semibold">{country.name}</div>
                            <div className="text-gray-500 text-xs">{country.ips.length} IP(s) detectee(s)</div>
                          </div>
                        </div>
                        <span
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{ backgroundColor: `${threatColor}20`, color: threatColor }}
                        >
                          {countryData ? getThreatLevelLabel(countryData.threatLevel) : 'Inconnu'}
                        </span>
                      </div>

                      {countryData && (
                        <div className="space-y-2">
                          {/* RGPD */}
                          <div className="flex items-center gap-2 text-sm">
                            <Scale className="w-4 h-4" style={{ color: countryData.gdpr?.color || '#6B7280' }} />
                            <span className="text-gray-400">RGPD:</span>
                            <span style={{ color: countryData.gdpr?.color || '#6B7280' }}>
                              {countryData.gdpr?.description || 'Non evalue'}
                            </span>
                          </div>

                          {/* Groupes de hackers */}
                          {countryData.hackerGroups && countryData.hackerGroups.length > 0 && (
                            <div className="flex items-start gap-2 text-sm">
                              <Users className="w-4 h-4 text-red-400 mt-0.5" />
                              <span className="text-gray-400">Hackers:</span>
                              <span className="text-red-400">{countryData.hackerGroups.length} groupe(s) connu(s)</span>
                            </div>
                          )}

                          {/* Strategie */}
                          {countryData.cyberStrategy && (
                            <div className="flex items-start gap-2 text-sm">
                              <Shield className="w-4 h-4 text-blue-400 mt-0.5" />
                              <span className="text-gray-400 truncate">{countryData.cyberStrategy}</span>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mt-3 pt-3 border-t border-gray-700/50">
                        <div className="flex flex-wrap gap-1">
                          {country.locations.slice(0, 3).map((loc, locIdx) => (
                            <IPTooltip key={locIdx} ip={loc.ip} location={loc}>
                              <span className="text-xs font-mono text-gray-400 bg-gray-800 px-2 py-0.5 rounded cursor-help hover:bg-cyan-500/20 hover:text-cyan-400 transition-colors">
                                {loc.ip}
                              </span>
                            </IPTooltip>
                          ))}
                          {country.locations.length > 3 && (
                            <span className="text-xs text-gray-500">+{country.locations.length - 3} autres</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="text-gray-500 text-sm mt-6 text-center">
              Cliquez sur un marqueur ou un pays pour voir les details (groupes de hackers, strategie cyber, conformite RGPD)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// AUTRES COMPOSANTS
// ==========================================

// Carte d'Action Prioritaire
const PriorityActionCard = ({ action, index }) => {
  const [expanded, setExpanded] = useState(false);

  const getPriorityConfig = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return {
          color: 'text-red-400',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/30',
          label: 'URGENT',
          labelBg: 'bg-gradient-to-r from-red-600 to-red-500',
          delay: 'Sous 48h',
          icon: AlertOctagon
        };
      case 'high':
        return {
          color: 'text-orange-400',
          bgColor: 'bg-orange-500/10',
          borderColor: 'border-orange-500/30',
          label: 'IMPORTANT',
          labelBg: 'bg-gradient-to-r from-orange-600 to-orange-500',
          delay: 'Sous 1 semaine',
          icon: AlertTriangle
        };
      case 'medium':
        return {
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-500/10',
          borderColor: 'border-yellow-500/30',
          label: 'A PLANIFIER',
          labelBg: 'bg-gradient-to-r from-yellow-600 to-yellow-500',
          delay: 'Sous 1 mois',
          icon: AlertCircle
        };
      default:
        return {
          color: 'text-blue-400',
          bgColor: 'bg-blue-500/10',
          borderColor: 'border-blue-500/30',
          label: 'RECOMMANDE',
          labelBg: 'bg-gradient-to-r from-blue-600 to-blue-500',
          delay: 'A votre convenance',
          icon: Info
        };
    }
  };

  const config = getPriorityConfig(action.severity);
  const IconComponent = config.icon;

  return (
    <div className={`${config.bgColor} ${config.borderColor} border rounded-xl overflow-hidden backdrop-blur-sm`}>
      <div
        className="p-5 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <span className={`${config.labelBg} text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg`}>
              {config.label}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-lg mb-2">
              {action.clientTitle || action.title || 'Action requise'}
            </h3>
            <p className="text-gray-400 text-sm">
              {action.clientDescription || action.description || 'Une action corrective est necessaire'}
            </p>
          </div>
          <div className="flex-shrink-0 text-right">
            <div className={`${config.color} text-sm font-medium`}>{config.delay}</div>
            <ChevronDown className={`w-5 h-5 text-gray-500 mt-2 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-800/50">
          <div className="pt-4 space-y-4">
            <div className="bg-black/30 rounded-lg p-4">
              <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-cyan-400" />
                Ce que cela signifie pour vous
              </h4>
              <p className="text-gray-400 text-sm">
                {action.clientImpact || 'Cette vulnerabilite pourrait permettre a un attaquant de compromettre vos systemes ou d\'acceder a vos donnees sensibles.'}
              </p>
            </div>

            <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/20">
              <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-green-400" />
                Ce que vous devez faire
              </h4>
              <p className="text-gray-400 text-sm">
                {action.clientSolution || action.solution || 'Contactez votre equipe informatique ou votre prestataire pour appliquer les correctifs necessaires.'}
              </p>
            </div>

            {action.cve && (
              <div className="flex items-center gap-2 pt-2">
                <span className="text-gray-500 text-xs">Reference technique:</span>
                <span className="text-cyan-400 text-xs font-mono bg-cyan-400/10 px-2 py-0.5 rounded">{action.cve}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Workflow d'attaque style n8n
const AttackWorkflowN8N = ({ vulnerabilities = [] }) => {
  // Generate workflow nodes based on vulnerabilities
  const generateWorkflowNodes = () => {
    const criticals = vulnerabilities.filter(v => v.severity === 'critical');
    const highs = vulnerabilities.filter(v => v.severity === 'high');
    const mediums = vulnerabilities.filter(v => v.severity === 'medium');

    // Noeuds du workflow
    const nodes = [
      {
        id: 'start',
        type: 'trigger',
        label: 'Attaquant',
        sublabel: 'Point d\'entree',
        icon: Crosshair,
        color: 'cyan',
        x: 0,
        y: 150
      },
      {
        id: 'recon',
        type: 'action',
        label: 'Reconnaissance',
        sublabel: 'Scan & Enumeration',
        icon: Globe,
        color: 'blue',
        x: 200,
        y: 150
      }
    ];

    let xPos = 400;
    let yOffset = 0;

    // Add critical vulnerabilities as exploitation nodes
    if (criticals.length > 0) {
      nodes.push({
        id: 'exploit-critical',
        type: 'exploit',
        label: 'Exploit Critique',
        sublabel: `${criticals.length} vuln(s) - ${criticals[0]?.title?.substring(0, 20) || 'CVE'}...`,
        icon: Skull,
        color: 'red',
        x: xPos,
        y: 80
      });
      yOffset = 70;
    }

    if (highs.length > 0) {
      nodes.push({
        id: 'exploit-high',
        type: 'exploit',
        label: 'Exploit Eleve',
        sublabel: `${highs.length} vuln(s)`,
        icon: AlertTriangle,
        color: 'orange',
        x: xPos,
        y: 150 + yOffset
      });
    }

    // Noeud de mouvement lateral
    nodes.push({
      id: 'lateral',
      type: 'action',
      label: 'Mouvement Lateral',
      sublabel: 'Propagation reseau',
      icon: Network,
      color: 'purple',
      x: 600,
      y: 150
    });

    // Noeud d'escalade de privileges
    nodes.push({
      id: 'privesc',
      type: 'action',
      label: 'Escalade Privileges',
      sublabel: 'Admin / Root',
      icon: ShieldAlert,
      color: 'yellow',
      x: 800,
      y: 80
    });

    // Noeud d'exfiltration
    nodes.push({
      id: 'exfil',
      type: 'danger',
      label: 'Exfiltration',
      sublabel: 'Vol de donnees',
      icon: Database,
      color: 'red',
      x: 800,
      y: 220
    });

    // Noeud final - Compromission
    nodes.push({
      id: 'compromised',
      type: 'end',
      label: 'Compromised System',
      sublabel: 'Controle total',
      icon: Skull,
      color: 'red',
      x: 1000,
      y: 150
    });

    return nodes;
  };

  const nodes = generateWorkflowNodes();

  // Connexions entre les noeuds
  const connections = [
    { from: 'start', to: 'recon' },
    { from: 'recon', to: 'exploit-critical' },
    { from: 'recon', to: 'exploit-high' },
    { from: 'exploit-critical', to: 'lateral' },
    { from: 'exploit-high', to: 'lateral' },
    { from: 'lateral', to: 'privesc' },
    { from: 'lateral', to: 'exfil' },
    { from: 'privesc', to: 'compromised' },
    { from: 'exfil', to: 'compromised' }
  ];

  const getNodeColor = (color) => {
    const colors = {
      cyan: { bg: 'bg-cyan-500/20', border: 'border-cyan-500', text: 'text-cyan-400', line: '#06b6d4' },
      blue: { bg: 'bg-blue-500/20', border: 'border-blue-500', text: 'text-blue-400', line: '#3b82f6' },
      red: { bg: 'bg-red-500/20', border: 'border-red-500', text: 'text-red-400', line: '#ef4444' },
      orange: { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-400', line: '#f97316' },
      yellow: { bg: 'bg-yellow-500/20', border: 'border-yellow-500', text: 'text-yellow-400', line: '#eab308' },
      purple: { bg: 'bg-purple-500/20', border: 'border-purple-500', text: 'text-purple-400', line: '#a855f7' },
      green: { bg: 'bg-green-500/20', border: 'border-green-500', text: 'text-green-400', line: '#22c55e' }
    };
    return colors[color] || colors.cyan;
  };

  return (
    <div className="bg-black/30 rounded-xl p-6 border border-gray-700/50 mt-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-purple-500/20">
          <Route className="w-5 h-5 text-purple-400" />
        </div>
        <div>
          <h4 className="text-white font-semibold">Chemin d'Attaque Identifie</h4>
          <p className="text-gray-500 text-sm">Workflow de compromission potentielle</p>
        </div>
      </div>

      {/* Workflow Canvas */}
      <div className="relative overflow-x-auto pb-4">
        <div className="relative" style={{ minWidth: '1100px', height: '320px' }}>
          {/* SVG pour les connexions */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
            <defs>
              <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" />
              </marker>
            </defs>
            {connections.map((conn, idx) => {
              const fromNode = nodes.find(n => n.id === conn.from);
              const toNode = nodes.find(n => n.id === conn.to);
              if (!fromNode || !toNode) return null;

              const x1 = fromNode.x + 140;
              const y1 = fromNode.y + 35;
              const x2 = toNode.x;
              const y2 = toNode.y + 35;

              // Courbe de Bezier pour un look n8n
              const midX = (x1 + x2) / 2;

              return (
                <path
                  key={idx}
                  d={`M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`}
                  fill="none"
                  stroke="#4b5563"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  markerEnd="url(#arrowhead)"
                  className="animate-pulse"
                  style={{ animationDuration: '3s' }}
                />
              );
            })}
          </svg>

          {/* Noeuds */}
          {nodes.map((node) => {
            const colors = getNodeColor(node.color);
            const Icon = node.icon;

            return (
              <div
                key={node.id}
                className={`absolute ${colors.bg} ${colors.border} border-2 rounded-xl p-3 shadow-lg backdrop-blur-sm transition-all hover:scale-105 hover:shadow-xl cursor-pointer`}
                style={{
                  left: node.x,
                  top: node.y,
                  width: '140px',
                  zIndex: 10
                }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={`p-1.5 rounded-lg ${colors.bg}`}>
                    <Icon className={`w-4 h-4 ${colors.text}`} />
                  </div>
                  <span className={`text-xs font-bold ${colors.text} uppercase tracking-wide`}>
                    {node.type === 'trigger' ? 'Trigger' : node.type === 'end' ? 'End' : 'Action'}
                  </span>
                </div>
                <div className="text-white text-sm font-semibold truncate">{node.label}</div>
                <div className="text-gray-400 text-xs truncate">{node.sublabel}</div>

                {/* Indicateurs de connexion style n8n */}
                <div className="absolute -left-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 rounded-full bg-gray-600 border-2 border-gray-800" />
                <div className="absolute -right-1.5 top-1/2 transform -translate-y-1/2 w-3 h-3 rounded-full bg-gray-600 border-2 border-gray-800" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Legende */}
      <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-gray-700/50">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-cyan-500" />
          <span className="text-xs text-gray-400">Point d'entree</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span className="text-xs text-gray-400">Exploitation critique</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-purple-500" />
          <span className="text-xs text-gray-400">Mouvement lateral</span>
        </div>
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4" viewBox="0 0 20 10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#6b7280" strokeWidth="2" strokeDasharray="3,3" />
          </svg>
          <span className="text-xs text-gray-400">Flux d'attaque</span>
        </div>
      </div>
    </div>
  );
};

// Carte de Document Telechargeable
const DocumentCard = ({ title, description, icon: IconComponent, formats, onDownload, primary }) => {
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (format) => {
    setDownloading(format);
    try {
      await onDownload(format);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className={`${primary ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-cyan-500/30' : 'bg-gradient-to-br from-gray-900/80 to-gray-800/40 border-gray-700/50'} backdrop-blur-sm border rounded-2xl p-5 hover:border-cyan-500/50 transition-all`}>
      <div className="flex items-start gap-4">
        <div className={`${primary ? 'bg-gradient-to-br from-cyan-500 to-blue-600' : 'bg-gray-800'} p-3 rounded-xl`}>
          <IconComponent className={`w-6 h-6 ${primary ? 'text-white' : 'text-gray-400'}`} />
        </div>
        <div className="flex-1">
          <h4 className="text-white font-semibold mb-1">{title}</h4>
          <p className="text-gray-400 text-sm mb-4">{description}</p>
          <div className="flex flex-wrap gap-2">
            {formats.map((format) => (
              <button
                key={format}
                onClick={() => handleDownload(format)}
                disabled={downloading === format}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  format === 'pdf'
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 hover:scale-105'
                    : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/30 hover:scale-105'
                } disabled:opacity-50 disabled:scale-100`}
              >
                {downloading === format ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {format.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// DETAILED CYBERSCORE COMPONENT
// ==========================================

const ShareCyberScoreSection = ({ scoreData, loading }) => {
  const [expandedComponents, setExpandedComponents] = useState({});
  const [showScoreTooltip, setShowScoreTooltip] = useState(false);

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/40 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8">
        <div className="animate-pulse flex items-center gap-6">
          <div className="w-28 h-28 rounded-full bg-slate-700" />
          <div className="flex-1 space-y-4">
            <div className="h-6 bg-slate-700 rounded w-1/3" />
            <div className="h-4 bg-slate-700 rounded w-1/2" />
            <div className="h-4 bg-slate-700 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!scoreData) return null;

  const getGradeColor = (grade) => {
    const colors = {
      'A+': { bg: 'from-green-400 to-green-600', text: 'text-green-400', border: 'border-green-500' },
      'A': { bg: 'from-green-500 to-green-700', text: 'text-green-400', border: 'border-green-500' },
      'B': { bg: 'from-blue-500 to-blue-700', text: 'text-blue-400', border: 'border-blue-500' },
      'C': { bg: 'from-yellow-500 to-yellow-700', text: 'text-yellow-400', border: 'border-yellow-500' },
      'D': { bg: 'from-orange-500 to-orange-700', text: 'text-orange-400', border: 'border-orange-500' },
      'E': { bg: 'from-red-500 to-red-700', text: 'text-red-400', border: 'border-red-500' },
      'F': { bg: 'from-red-600 to-red-800', text: 'text-red-400', border: 'border-red-500' }
    };
    return colors[grade] || colors['C'];
  };

  const getProbabilityColor = (prob) => {
    if (prob >= 80) return { color: '#EF4444', label: 'CRITIQUE', bg: 'bg-red-500/20 border-red-500' };
    if (prob >= 60) return { color: '#F97316', label: 'ELEVE', bg: 'bg-orange-500/20 border-orange-500' };
    if (prob >= 40) return { color: '#F59E0B', label: 'MOYEN', bg: 'bg-yellow-500/20 border-yellow-500' };
    if (prob >= 20) return { color: '#3B82F6', label: 'FAIBLE', bg: 'bg-blue-500/20 border-blue-500' };
    return { color: '#10B981', label: 'MINIMAL', bg: 'bg-green-500/20 border-green-500' };
  };

  const getSeverityStyle = (severity) => {
    const styles = {
      'critical': 'text-red-400 bg-red-500/20 border-red-500/30',
      'high': 'text-orange-400 bg-orange-500/20 border-orange-500/30',
      'medium': 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30',
      'low': 'text-blue-400 bg-blue-500/20 border-blue-500/30',
      'info': 'text-gray-400 bg-gray-500/20 border-gray-500/30'
    };
    return styles[severity] || styles['info'];
  };

  const getComponentIcon = (key) => {
    const iconMap = {
      'ssl_tls': Lock,
      'security_headers': Shield,
      'exposed_services': Server,
      'vulnerabilities': Bug,
      'osint_exposure': Globe,
      'web_security': Globe,
      'network_exposure': Wifi,
      'services_security': Server,
      'misconfigurations': AlertTriangle,
      'domain_security': Shield,
      'device_exposure': Server,
      'communication_security': Lock
    };
    return iconMap[key] || Shield;
  };

  const gradeColors = getGradeColor(scoreData.grade);
  const probColors = getProbabilityColor(scoreData.hack_probability);

  const toggleComponent = (key) => {
    setExpandedComponents(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-6">
      {/* Main Score Card */}
      <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/40 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8 shadow-xl">
        <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-8">
          {/* Grade Circle */}
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className={`w-28 h-28 rounded-full bg-gradient-to-br ${gradeColors.bg} flex items-center justify-center shadow-lg`}>
                <span className="text-5xl font-black text-white drop-shadow-lg">{scoreData.grade}</span>
              </div>
              <div className="absolute -bottom-2 -right-2 bg-slate-800 rounded-full px-3 py-1.5 border border-slate-600 shadow-lg flex items-center gap-1.5">
                <span className="text-lg font-bold text-white">{scoreData.total_score}/100</span>
                {/* Info Tooltip Icon */}
                <div className="relative">
                  <button
                    onMouseEnter={() => setShowScoreTooltip(true)}
                    onMouseLeave={() => setShowScoreTooltip(false)}
                    onClick={() => setShowScoreTooltip(!showScoreTooltip)}
                    className="p-1 rounded-full hover:bg-slate-700 transition-colors"
                  >
                    <Info className="w-4 h-4 text-cyan-400" />
                  </button>
                  {/* Tooltip */}
                  {showScoreTooltip && (scoreData.score_explanations?.length > 0 || scoreData.vulnerability_breakdown) && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-80 bg-slate-900 border border-slate-600 rounded-xl p-4 shadow-2xl z-50">
                      <div className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                        <Brain className="w-4 h-4 text-cyan-400" />
                        Explication du Score
                      </div>
                      {scoreData.score_explanations?.map((exp, idx) => (
                        <p key={idx} className="text-xs text-gray-300 mb-2">{exp}</p>
                      ))}
                      {scoreData.vulnerability_breakdown && (
                        <div className="mt-3 pt-3 border-t border-slate-700">
                          <div className="text-xs font-semibold text-gray-400 mb-2">Repartition des vulnerabilites:</div>
                          <div className="grid grid-cols-2 gap-2">
                            {scoreData.vulnerability_breakdown.critical > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full bg-red-500" />
                                <span className="text-xs text-gray-300">{scoreData.vulnerability_breakdown.critical} Critiques</span>
                              </div>
                            )}
                            {scoreData.vulnerability_breakdown.high > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full bg-orange-500" />
                                <span className="text-xs text-gray-300">{scoreData.vulnerability_breakdown.high} Hautes</span>
                              </div>
                            )}
                            {scoreData.vulnerability_breakdown.medium > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full bg-yellow-500" />
                                <span className="text-xs text-gray-300">{scoreData.vulnerability_breakdown.medium} Moyennes</span>
                              </div>
                            )}
                            {scoreData.vulnerability_breakdown.low > 0 && (
                              <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full bg-blue-500" />
                                <span className="text-xs text-gray-300">{scoreData.vulnerability_breakdown.low} Faibles</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
                        <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-slate-600" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                <Shield className="w-6 h-6 text-cyan-400" />
                Detailed CyberScore
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                Analyse {scoreData.scan_type?.replace(/_/g, ' ').toUpperCase()}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {scoreData.tools_analyzed?.length || 0} outils analyses
              </p>
              {/* Vulnerability summary */}
              {scoreData.vulnerability_breakdown && scoreData.vulnerability_breakdown.total > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-gray-500">{scoreData.vulnerability_breakdown.total} vulnerabilites:</span>
                  {scoreData.vulnerability_breakdown.critical > 0 && (
                    <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">{scoreData.vulnerability_breakdown.critical} crit</span>
                  )}
                  {scoreData.vulnerability_breakdown.high > 0 && (
                    <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">{scoreData.vulnerability_breakdown.high} high</span>
                  )}
                  {scoreData.vulnerability_breakdown.medium > 0 && (
                    <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">{scoreData.vulnerability_breakdown.medium} med</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Hack Probability Gauge */}
          <div className="flex flex-col items-center">
            <div className="text-sm text-gray-400 mb-3">Probabilite de Piratage</div>
            <div className="relative w-28 h-28">
              <svg className="w-28 h-28 transform -rotate-90">
                <circle cx="56" cy="56" r="48" stroke="#1e293b" strokeWidth="10" fill="none" />
                <circle
                  cx="56"
                  cy="56"
                  r="48"
                  stroke={probColors.color}
                  strokeWidth="10"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${(scoreData.hack_probability / 100) * 301.6} 301.6`}
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold" style={{ color: probColors.color }}>
                  {scoreData.hack_probability}%
                </span>
              </div>
            </div>
            <span className={`mt-3 px-3 py-1.5 rounded-full text-xs font-bold border ${probColors.bg}`} style={{ color: probColors.color }}>
              {probColors.label}
            </span>
          </div>
        </div>

        {/* Summary */}
        {scoreData.summary && (
          <div className="mt-6 p-4 bg-black/30 rounded-xl border border-gray-700/50">
            <div className="flex items-start gap-3">
              <Brain className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
              <MarkdownRenderer content={scoreData.summary} variant="compact" />
            </div>
          </div>
        )}
      </div>

      {/* Security Components Breakdown */}
      {scoreData.components && Object.keys(scoreData.components).length > 0 && (
        <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/40 backdrop-blur-sm border border-gray-700/50 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-gray-700/50">
            <h4 className="text-lg font-bold text-white flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/20">
                <PieChart className="w-5 h-5 text-cyan-400" />
              </div>
              Analyzed Security Components
            </h4>
          </div>
          <div className="divide-y divide-gray-700/50">
            {Object.entries(scoreData.components).map(([key, component]) => {
              const IconComp = getComponentIcon(key);
              const isExpanded = expandedComponents[key];

              return (
                <div key={key} className="bg-slate-800/30">
                  <button
                    onClick={() => toggleComponent(key)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2.5 rounded-xl border ${getSeverityStyle(component.severity)}`}>
                        <IconComp className="w-5 h-5" />
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-semibold text-white">{component.name}</div>
                        <div className="text-xs text-gray-500">
                          Poids: {Math.round(component.weight * 100)}% | {component.issues_count} probleme{component.issues_count > 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* Score Bar */}
                      <div className="w-32 h-2.5 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            component.score >= 80 ? 'bg-green-500' :
                            component.score >= 60 ? 'bg-blue-500' :
                            component.score >= 40 ? 'bg-yellow-500' :
                            component.score >= 20 ? 'bg-orange-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${component.score}%` }}
                        />
                      </div>
                      <span className="text-sm font-bold text-white w-10 text-right">{component.score}</span>
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {/* Expanded Findings */}
                  {isExpanded && component.findings && component.findings.length > 0 && (
                    <div className="px-6 pb-4">
                      <div className="ml-14 space-y-1">
                        {component.findings.map((finding, idx) => {
                          // Check if this is a sub-item (starts with arrow or spaces)
                          const isSubItem = finding.trim().startsWith('→') || finding.startsWith('  ');
                          const isEllipsis = finding.includes('...');

                          if (isSubItem) {
                            return (
                              <div
                                key={idx}
                                className="flex items-start gap-2 text-sm pl-8 py-1"
                              >
                                <span className="text-brand-green">→</span>
                                <span className="text-gray-400 font-mono text-xs break-all">
                                  {finding.replace(/^\s*→\s*/, '').trim()}
                                </span>
                              </div>
                            );
                          }

                          return (
                            <div
                              key={idx}
                              className={`flex items-start gap-3 text-sm p-3 bg-slate-900/50 rounded-lg border border-slate-700/50 ${idx > 0 ? 'mt-2' : ''}`}
                            >
                              <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-300">{finding}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AI Recommendations */}
      {scoreData.recommendations && scoreData.recommendations.length > 0 && (
        <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/40 backdrop-blur-sm border border-gray-700/50 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-gray-700/50">
            <h4 className="text-lg font-bold text-white flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <Lightbulb className="w-5 h-5 text-green-400" />
              </div>
              Recommandations IA Prioritaires
            </h4>
          </div>
          <div className="p-6 space-y-3">
            {scoreData.recommendations.map((rec, idx) => (
              <div
                key={idx}
                className="flex items-start gap-4 p-4 bg-green-500/5 rounded-xl border border-green-500/20"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                  <span className="text-sm font-bold text-green-400">{idx + 1}</span>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed">{rec}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tools Analyzed */}
      {scoreData.tools_analyzed && scoreData.tools_analyzed.length > 0 && (
        <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/40 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6">
          <h4 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2">
            <Terminal className="w-4 h-4" />
            Outils d'analyse utilises
          </h4>
          <div className="flex flex-wrap gap-2">
            {scoreData.tools_analyzed.map((tool, idx) => (
              <span
                key={idx}
                className="px-3 py-1.5 text-xs bg-slate-700/50 text-slate-300 rounded-full border border-slate-600/50"
              >
                {tool}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Section Accordeon
const AccordionSection = ({ title, icon: IconComponent, iconColor, iconBg, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/40 backdrop-blur-sm border border-gray-700/50 rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${iconBg || 'bg-gray-800'}`}>
            <IconComponent className={`w-6 h-6 ${iconColor}`} />
          </div>
          <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>
        <ChevronDown className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-gray-700/50 p-6">
          {children}
        </div>
      )}
    </div>
  );
};

// ==========================================
// COMPOSANT PRINCIPAL
// ==========================================

const SharePage = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);
  const [workspaceData, setWorkspaceData] = useState(null);
  const [cyberScoreData, setCyberScoreData] = useState(null);
  const [cyberScoreLoading, setCyberScoreLoading] = useState(false);

  // Auth states
  const [authStep, setAuthStep] = useState('loading');
  const [password, setPassword] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Fetch share info
  useEffect(() => {
    const fetchShareInfo = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/v1/workspace-share/access/${token}`);
        setShareInfo(response.data.share);

        if (response.data.share.auth_method === 'none') {
          await fetchWorkspace();
        } else {
          setAuthStep('auth');
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Ce lien de partage n\'existe pas.');
        } else if (err.response?.status === 410) {
          setError(err.response.data?.detail || 'Ce partage a expire ou a ete revoque.');
        } else {
          setError('Erreur de chargement du partage.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchShareInfo();
  }, [token]);

  const fetchWorkspace = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/workspace-share/access/${token}/workspace`);
      setWorkspaceData(response.data);
      setAuthStep('workspace');

      // Fetch CyberScore for completed scans
      const scans = response.data?.scans || [];
      const completedScans = scans.filter(s => s.status === 'completed');
      if (completedScans.length > 0) {
        fetchCyberScore(completedScans[0].id);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Authentification requise');
        setAuthStep('auth');
      } else {
        setError(err.response?.data?.detail || 'Erreur de chargement');
      }
    }
  };

  const fetchCyberScore = async (scanId) => {
    if (!scanId) return;
    setCyberScoreLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/v1/workspace-share/access/${token}/cyberscore/${scanId}`);
      setCyberScoreData(response.data);
    } catch (err) {
      console.error('Failed to fetch CyberScore:', err);
    } finally {
      setCyberScoreLoading(false);
    }
  };

  const handleVerifyPassword = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/v1/workspace-share/access/${token}/verify-password`,
        { password }
      );

      setPasswordVerified(true);

      if (response.data.requires_sms) {
        await requestSmsCode();
      } else {
        await fetchWorkspace();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Mot de passe incorrect');
    } finally {
      setVerifying(false);
    }
  };

  const requestSmsCode = async () => {
    setSmsLoading(true);
    try {
      await axios.post(`${API_URL}/api/v1/workspace-share/access/${token}/request-sms`, {});
      setSmsSent(true);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur d\'envoi SMS');
    } finally {
      setSmsLoading(false);
    }
  };

  const handleVerifySms = async (e) => {
    e.preventDefault();
    setVerifying(true);

    try {
      await axios.post(`${API_URL}/api/v1/workspace-share/access/${token}/verify-sms`, { code: smsCode });
      setSmsVerified(true);
      await fetchWorkspace();
    } catch (err) {
      setError(err.response?.data?.detail || 'Code SMS incorrect');
    } finally {
      setVerifying(false);
    }
  };

  // Download handlers - uses the share endpoint for download
  const handleDownloadReport = async (scanId, format) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/v1/workspace-share/access/${token}/export-report/${scanId}?format=${format}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-audit-${scanId}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Download error. Please try again.');
    }
  };

  const handleDownloadGeoIP = async (format) => {
    try {
      const ws = workspaceData?.workspace;
      const response = await axios.post(
        `${API_URL}/api/v1/workspace-share/access/${token}/export-cartography`,
        {},
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cartographie-risques-${ws?.name || 'rapport'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('GeoIP download failed:', err);
    }
  };

  // Download Gamma reports (via share endpoint)
  const handleDownloadGammaReport = async (filename) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/v1/workspace-share/access/${token}/gamma-report/${encodeURIComponent(filename)}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Gamma report download failed:', err);
      alert('Report download error. Please try again.');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 rounded-full border-4 border-cyan-500/20 border-t-cyan-500 animate-spin" />
            <Shield className="w-8 h-8 text-cyan-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-gray-400 mt-6">Chargement de votre rapport...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !shareInfo) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-3xl p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
            <XCircle className="w-10 h-10 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Rapport non disponible</h2>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  // Auth step
  if (authStep === 'auth' && shareInfo) {
    const needsPassword = ['password', 'both'].includes(shareInfo.auth_method) && !passwordVerified;
    const needsSms = ['sms', 'both'].includes(shareInfo.auth_method) && !smsVerified;

    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-purple-500/5" />
        <div className="max-w-md w-full relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <span className="text-3xl font-bold text-white">SYLink</span>
            </div>
            <h1 className="text-xl text-white font-semibold">Security Audit Report</h1>
            <p className="text-gray-400 mt-2">{shareInfo.name || 'Secure access'}</p>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-3xl p-8">
            {needsPassword && (
              <form onSubmit={handleVerifyPassword} className="space-y-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                    <Lock className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Protected access</h3>
                    <p className="text-gray-500 text-sm">Entrez le mot de passe fourni</p>
                  </div>
                </div>

                {error && (
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}

                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-black/50 border border-gray-700 rounded-xl px-5 py-4 pr-14 text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                    placeholder="Mot de passe"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={verifying || !password}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold py-4 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-cyan-500/25"
                >
                  {verifying ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
                  {verifying ? 'Verifying...' : 'Access report'}
                </button>
              </form>
            )}

            {passwordVerified && needsSms && !smsSent && (
              <div className="space-y-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Password verified</h3>
                    <p className="text-gray-500 text-sm">Additional verification required</p>
                  </div>
                </div>

                <button
                  onClick={requestSmsCode}
                  disabled={smsLoading}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold py-4 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-cyan-500/25"
                >
                  {smsLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Smartphone className="w-5 h-5" />}
                  {smsLoading ? 'Envoi...' : 'Recevoir le code SMS'}
                </button>
              </div>
            )}

            {smsSent && (
              <form onSubmit={handleVerifySms} className="space-y-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                    <Smartphone className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Code sent</h3>
                    <p className="text-gray-500 text-sm">Enter the code received by SMS</p>
                  </div>
                </div>

                {error && (
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}

                <input
                  type="text"
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full bg-black/50 border border-gray-700 rounded-xl px-5 py-4 text-white text-center text-3xl tracking-[0.5em] font-mono focus:outline-none focus:border-cyan-500/50"
                  placeholder="000000"
                  maxLength={6}
                  required
                />

                <button
                  type="submit"
                  disabled={verifying || smsCode.length < 6}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold py-4 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-cyan-500/25"
                >
                  {verifying ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
                  {verifying ? 'Verifying...' : 'Access report'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // VUE PRINCIPALE DU RAPPORT CLIENT
  // ==========================================
  if (authStep === 'workspace' && workspaceData) {
    const ws = workspaceData.workspace;
    const scans = workspaceData.scans || [];
    const hosts = workspaceData.hosts || [];
    const vulnerabilities = workspaceData.vulnerabilities || [];
    const contracts = workspaceData.contracts || [];
    const summary = workspaceData.summary || {};
    const stats = workspaceData.stats || {};
    const aiAnalysis = workspaceData.ai_analysis || null;
    const gammaReports = workspaceData.gamma_reports || [];

    // Calculer le score et les stats avec la meme formule que le backend
    const criticalCount = stats.critical_count || aiAnalysis?.key_findings?.critical_count || 0;
    const highCount = stats.high_count || aiAnalysis?.key_findings?.high_count || 0;
    const mediumCount = stats.medium_count || aiAnalysis?.key_findings?.medium_count || 0;
    const lowCount = stats.low_count || aiAnalysis?.key_findings?.low_count || 0;

    // Calcul du CyberScore avec la meme formule que le backend
    // Base 100, -25 par critique, -15 par high, -8 par medium, -3 par low
    const calculatedScore = Math.max(0, Math.min(100,
      100 - (criticalCount * 25) - (highCount * 15) - (mediumCount * 8) - (lowCount * 3)
    ));
    // Utiliser cyberScoreData si disponible, sinon le score calcule
    const securityScore = cyberScoreData?.total_score ?? calculatedScore;
    const completedScans = scans.filter(s => s.status === 'completed');

    // Transformer les vulnerabilites en actions prioritaires
    const priorityActions = vulnerabilities
      .filter(v => v.severity === 'critical' || v.severity === 'high' || v.severity === 'medium')
      .sort((a, b) => {
        const order = { critical: 0, high: 1, medium: 2, low: 3 };
        return (order[a.severity?.toLowerCase()] || 4) - (order[b.severity?.toLowerCase()] || 4);
      })
      .slice(0, 10)
      .map(v => ({
        ...v,
        clientTitle: getClientFriendlyTitle(v),
        clientDescription: getClientFriendlyDescription(v),
        clientImpact: getClientFriendlyImpact(v),
        clientSolution: getClientFriendlySolution(v)
      }));

    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        {/* Background effects */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
        </div>

        {/* Header */}
        <header className="bg-black/50 backdrop-blur-md border-b border-gray-800 px-4 py-4 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-white">SYLink</span>
                <span className="hidden sm:inline text-gray-500 text-sm ml-2">Pentest</span>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-sm text-green-400 font-medium">Consultation securisee</span>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="max-w-7xl mx-auto px-4 py-8 relative z-10">
          {/* Hero Header avec CyberScore */}
          <HeroHeader
            workspace={ws}
            stats={{ critical_count: criticalCount, high_count: highCount, medium_count: mediumCount }}
            vulnerabilities={vulnerabilities}
            score={securityScore}
          />

          {/* Section Detailed CyberScore */}
          {(cyberScoreData || cyberScoreLoading) && (
            <section className="mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-xl bg-cyan-500/20">
                  <BarChart3 className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Analyse Detailed CyberScoree</h2>
                  <p className="text-gray-400">Evaluation complete de votre posture de securite par l'IA</p>
                </div>
              </div>
              <ShareCyberScoreSection scoreData={cyberScoreData} loading={cyberScoreLoading} />
            </section>
          )}

          {/* Section 1: Actions Prioritaires */}
          {priorityActions.length > 0 && (
            <section className="mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-xl bg-red-500/20">
                  <Target className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Vos Actions Prioritaires</h2>
                  <p className="text-gray-400">Ce que vous devez faire pour ameliorer votre securite</p>
                </div>
              </div>

              <div className="space-y-4">
                {priorityActions.map((action, idx) => (
                  <PriorityActionCard key={idx} action={action} index={idx} />
                ))}
              </div>

              {vulnerabilities.length > 10 && (
                <p className="text-gray-500 text-sm mt-4 text-center">
                  + {vulnerabilities.length - 10} autres actions dans le rapport complet
                </p>
              )}
            </section>
          )}

          {/* Section 2: Documents Telechargables - Style Workspace */}
          <section className="mb-8">
            <div className="bg-gray-900/50 rounded-xl border border-gray-700/50 overflow-hidden">
              {/* Header */}
              <div className="p-4 flex items-center justify-between border-b border-gray-800">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  Rapports & Documents
                  {(completedScans.length + gammaReports.length + contracts.filter(c => c.status === 'signed').length + 1) > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full">
                      {completedScans.length + gammaReports.length + contracts.filter(c => c.status === 'signed').length + 1}
                    </span>
                  )}
                </h3>
              </div>

              <div className="p-6 space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="text-center p-2 bg-black/30 rounded border border-gray-800">
                    <div className="text-xl font-bold text-orange-400">{completedScans.length}</div>
                    <div className="text-xs text-gray-500">Rapports de Scan</div>
                  </div>
                  <div className="text-center p-2 bg-black/30 rounded border border-gray-800">
                    <div className="text-xl font-bold text-cyan-400">1</div>
                    <div className="text-xs text-gray-500">Rapport GeoIP</div>
                  </div>
                  <div className="text-center p-2 bg-black/30 rounded border border-gray-800">
                    <div className="text-xl font-bold text-purple-400">{gammaReports.length}</div>
                    <div className="text-xs text-gray-500">Rapports IA</div>
                  </div>
                  <div className="text-center p-2 bg-black/30 rounded border border-gray-800">
                    <div className="text-xl font-bold text-green-400">{contracts.filter(c => c.status === 'signed').length}</div>
                    <div className="text-xs text-gray-500">Contrats</div>
                  </div>
                </div>

                {/* Scan Reports Section */}
                {completedScans.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4 text-orange-400" />
                      Rapports de Scan ({completedScans.length})
                    </h4>
                    <div className="space-y-2">
                      {completedScans.map((scan) => (
                        <div
                          key={scan.id}
                          className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-gray-800 hover:border-orange-500/30 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-orange-500/20">
                              <Target className="w-5 h-5 text-orange-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white truncate max-w-xs">
                                Rapport - {scan.name || scan.target}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span className="capitalize">{scan.scan_type || 'pentest'}</span>
                                <span>•</span>
                                <span>{new Date(scan.completed_at || scan.created_at).toLocaleDateString('fr-FR')}</span>
                                {scan.vulnerabilities_count > 0 && (
                                  <>
                                    <span>•</span>
                                    <span className="text-red-400">{scan.vulnerabilities_count} vulns</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleDownloadReport(scan.id, 'pdf')}
                              className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/30 transition-colors flex items-center gap-1"
                              title="Telecharger en PDF"
                            >
                              <FileText className="w-3 h-3" />
                              PDF
                            </button>
                            <button
                              onClick={() => handleDownloadReport(scan.id, 'docx')}
                              className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-xs font-medium hover:bg-blue-500/30 transition-colors flex items-center gap-1"
                              title="Telecharger en Word"
                            >
                              <FileText className="w-3 h-3" />
                              DOCX
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* GeoIP Report Section */}
                <div>
                  <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-cyan-400" />
                    Rapport Cartographie des Risques
                  </h4>
                  <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-gray-800 hover:border-cyan-500/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-cyan-500/20">
                        <Globe className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">
                          Rapport GeoIP & Conformite
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>Geolocalisation IPs</span>
                          <span>•</span>
                          <span>Risques pays</span>
                          <span>•</span>
                          <span>RGPD</span>
                          <span>•</span>
                          <span>Groupes de hackers</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={handleDownloadGeoIP}
                      className="px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg text-xs font-medium hover:bg-cyan-500/30 transition-colors flex items-center gap-1"
                      title="Telecharger le rapport GeoIP"
                    >
                      <Download className="w-3 h-3" />
                      PDF
                    </button>
                  </div>
                </div>

                {/* AI Generated Reports (Gamma) */}
                {gammaReports.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      Generated AI Reports ({gammaReports.length})
                    </h4>
                    <div className="space-y-2">
                      {gammaReports.map((report) => {
                        const files = report.files || {};
                        const formats = Object.keys(files);
                        const reportType = report.report_type?.replace(/_/g, ' ') || 'rapport';

                        return (
                          <div
                            key={report.id}
                            className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-gray-800 hover:border-purple-500/30 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-purple-500/20">
                                <Sparkles className="w-5 h-5 text-purple-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-white truncate max-w-xs">
                                  {report.name || report.title || 'Rapport IA'}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <span className="capitalize">{reportType}</span>
                                  <span>•</span>
                                  <span>{new Date(report.generated_at || report.created_at).toLocaleDateString('fr-FR')}</span>
                                  <span>•</span>
                                  <span className="text-green-400">Pret</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {formats.length > 0 ? (
                                formats.map((fmt) => (
                                  <button
                                    key={fmt}
                                    onClick={() => handleDownloadGammaReport(files[fmt])}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
                                      fmt === 'pdf'
                                        ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                                        : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                                    }`}
                                    title={`Telecharger en ${fmt.toUpperCase()}`}
                                  >
                                    <FileText className="w-3 h-3" />
                                    {fmt.toUpperCase()}
                                  </button>
                                ))
                              ) : (
                                <button
                                  onClick={() => handleDownloadGammaReport(report.name)}
                                  className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/30 transition-colors flex items-center gap-1"
                                  title="Telecharger"
                                >
                                  <Download className="w-3 h-3" />
                                  PDF
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Signed Contracts */}
                {contracts.filter(c => c.status === 'signed').length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <FileCheck className="w-4 h-4 text-green-400" />
                      Contrats Signes ({contracts.filter(c => c.status === 'signed').length})
                    </h4>
                    <div className="space-y-2">
                      {contracts.filter(c => c.status === 'signed').map((contract) => (
                        <div
                          key={contract.id}
                          className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-gray-800 hover:border-green-500/30 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-green-500/20">
                              <FileCheck className="w-5 h-5 text-green-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white truncate max-w-xs">
                                {contract.title || 'Contrat de Prestation'}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span>Signe le {new Date(contract.signed_at).toLocaleDateString('fr-FR')}</span>
                                <span>•</span>
                                <span className="text-green-400">Valide</span>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {}}
                            className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-xs font-medium hover:bg-green-500/30 transition-colors flex items-center gap-1"
                            title="Telecharger le contrat"
                          >
                            <Download className="w-3 h-3" />
                            PDF
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {completedScans.length === 0 && gammaReports.length === 0 && contracts.filter(c => c.status === 'signed').length === 0 && (
                  <div className="bg-black/30 rounded-lg p-6 border border-gray-800 text-center">
                    <FileText className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                    <p className="text-gray-500 text-sm">
                      Aucun document disponible
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Section 3: AI Summary */}
          {aiAnalysis && (
            <section className="mb-8">
              <AccordionSection
                title="AI Analysis Summary"
                icon={Brain}
                iconColor="text-purple-400"
                iconBg="bg-purple-500/20"
                defaultOpen={true}
              >
                <div className="space-y-6">
                  {aiAnalysis.ai_analysis?.content && (
                    <div className="bg-black/30 rounded-xl p-6 border border-purple-500/20">
                      <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-purple-400" />
                        Ce que l'IA a identifie
                      </h4>
                      <pre className="whitespace-pre-wrap text-gray-300 font-sans text-sm leading-relaxed max-h-[300px] overflow-y-auto">
                        {aiAnalysis.ai_analysis.content}
                      </pre>
                    </div>
                  )}

                  {aiAnalysis.ai_solutions?.content && (
                    <div className="bg-green-500/5 rounded-xl p-6 border border-green-500/20">
                      <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-green-400" />
                        Plan d'action recommande
                      </h4>
                      <pre className="whitespace-pre-wrap text-gray-300 font-sans text-sm leading-relaxed">
                        {aiAnalysis.ai_solutions.content}
                      </pre>
                    </div>
                  )}
                </div>
              </AccordionSection>
            </section>
          )}

          {/* Section 3.5: Arbre d'Attaques & Chemins */}
          {vulnerabilities.length > 0 && (
            <section className="mb-8">
              <AccordionSection
                title="Arbre d'Attaques & Chemins d'Exploitation"
                icon={Route}
                iconColor="text-orange-400"
                iconBg="bg-orange-500/20"
                defaultOpen={false}
              >
                <div className="space-y-6">
                  {/* Explication */}
                  <div className="bg-orange-500/5 rounded-xl p-4 border border-orange-500/20">
                    <p className="text-gray-300 text-sm">
                      Cette visualisation montre comment un attaquant pourrait exploiter les vulnerabilites detectees pour compromettre vos systemes.
                      Chaque chemin represente une sequence d'attaque potentielle.
                    </p>
                  </div>

                  {/* Visualisation simplifiee des chemins d'attaque */}
                  <div className="bg-black/30 rounded-xl p-6 border border-gray-700/50">
                    <h4 className="text-white font-semibold mb-6 flex items-center gap-2">
                      <Network className="w-5 h-5 text-orange-400" />
                      Chemins d'attaque identifies
                    </h4>

                    <div className="space-y-4">
                      {/* Chemin 1: Vulnerabilites critiques */}
                      {vulnerabilities.filter(v => v.severity === 'critical').length > 0 && (
                        <div className="relative">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center">
                              <Target className="w-5 h-5 text-red-400" />
                            </div>
                            <div className="h-0.5 flex-1 bg-gradient-to-r from-red-500 to-red-500/20" />
                            <div className="px-3 py-1 bg-red-500/20 border border-red-500/50 rounded-full">
                              <span className="text-red-400 text-xs font-bold">CHEMIN CRITIQUE</span>
                            </div>
                          </div>
                          <div className="ml-5 pl-6 border-l-2 border-red-500/30 space-y-3">
                            {vulnerabilities.filter(v => v.severity === 'critical').slice(0, 3).map((vuln, idx) => (
                              <div key={idx} className="flex items-center gap-3 p-3 bg-red-500/5 rounded-lg border border-red-500/20">
                                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                                <div className="flex-1">
                                  <div className="text-white text-sm font-medium">{vuln.title || vuln.name}</div>
                                  <div className="text-gray-500 text-xs">{vuln.host || vuln.ip_address}:{vuln.port}</div>
                                </div>
                                <ArrowRight className="w-4 h-4 text-red-400" />
                              </div>
                            ))}
                            <div className="flex items-center gap-3 p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                              <Crosshair className="w-5 h-5 text-red-400" />
                              <span className="text-red-400 font-medium">Compromission complete du systeme</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Chemin 2: Vulnerabilites elevees */}
                      {vulnerabilities.filter(v => v.severity === 'high').length > 0 && (
                        <div className="relative mt-6">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-orange-500/20 border-2 border-orange-500 flex items-center justify-center">
                              <Target className="w-5 h-5 text-orange-400" />
                            </div>
                            <div className="h-0.5 flex-1 bg-gradient-to-r from-orange-500 to-orange-500/20" />
                            <div className="px-3 py-1 bg-orange-500/20 border border-orange-500/50 rounded-full">
                              <span className="text-orange-400 text-xs font-bold">CHEMIN ELEVE</span>
                            </div>
                          </div>
                          <div className="ml-5 pl-6 border-l-2 border-orange-500/30 space-y-3">
                            {vulnerabilities.filter(v => v.severity === 'high').slice(0, 3).map((vuln, idx) => (
                              <div key={idx} className="flex items-center gap-3 p-3 bg-orange-500/5 rounded-lg border border-orange-500/20">
                                <div className="w-2 h-2 rounded-full bg-orange-500" />
                                <div className="flex-1">
                                  <div className="text-white text-sm font-medium">{vuln.title || vuln.name}</div>
                                  <div className="text-gray-500 text-xs">{vuln.host || vuln.ip_address}:{vuln.port}</div>
                                </div>
                                <ArrowRight className="w-4 h-4 text-orange-400" />
                              </div>
                            ))}
                            <div className="flex items-center gap-3 p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
                              <Database className="w-5 h-5 text-orange-400" />
                              <span className="text-orange-400 font-medium">Acces aux donnees sensibles</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Chemin 3: Escalade de privileges */}
                      {vulnerabilities.length >= 2 && (
                        <div className="relative mt-6">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-purple-500/20 border-2 border-purple-500 flex items-center justify-center">
                              <Zap className="w-5 h-5 text-purple-400" />
                            </div>
                            <div className="h-0.5 flex-1 bg-gradient-to-r from-purple-500 to-purple-500/20" />
                            <div className="px-3 py-1 bg-purple-500/20 border border-purple-500/50 rounded-full">
                              <span className="text-purple-400 text-xs font-bold">ESCALADE DE PRIVILEGES</span>
                            </div>
                          </div>
                          <div className="ml-5 pl-6 border-l-2 border-purple-500/30">
                            <div className="flex items-start gap-4 p-4 bg-purple-500/5 rounded-lg border border-purple-500/20">
                              <div className="space-y-2 flex-1">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-xs text-gray-400">1</div>
                                  <span className="text-gray-300 text-sm">Acces initial via vulnerabilite</span>
                                </div>
                                <div className="ml-3 border-l-2 border-dashed border-purple-500/30 h-4" />
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-xs text-gray-400">2</div>
                                  <span className="text-gray-300 text-sm">Mouvement lateral sur le reseau</span>
                                </div>
                                <div className="ml-3 border-l-2 border-dashed border-purple-500/30 h-4" />
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-xs text-white">3</div>
                                  <span className="text-purple-400 text-sm font-medium">Elevation vers administrateur</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Risk summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30 text-center">
                      <div className="text-3xl font-bold text-red-400">{vulnerabilities.filter(v => v.severity === 'critical').length}</div>
                      <div className="text-gray-400 text-sm">Chemins critiques</div>
                    </div>
                    <div className="bg-orange-500/10 rounded-xl p-4 border border-orange-500/30 text-center">
                      <div className="text-3xl font-bold text-orange-400">{vulnerabilities.filter(v => v.severity === 'high').length}</div>
                      <div className="text-gray-400 text-sm">Chemins eleves</div>
                    </div>
                    <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30 text-center">
                      <div className="text-3xl font-bold text-yellow-400">{vulnerabilities.filter(v => v.severity === 'medium').length}</div>
                      <div className="text-gray-400 text-sm">Chemins moyens</div>
                    </div>
                  </div>
                </div>
              </AccordionSection>
            </section>
          )}

          {/* Section 4: Comprendre votre Audit */}
          <section className="mb-8">
            <AccordionSection
              title="Comprendre votre Audit"
              icon={BookOpen}
              iconColor="text-blue-400"
              iconBg="bg-blue-500/20"
            >
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-black/30 rounded-xl p-6 border border-gray-700/50 text-center">
                    <Server className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                    <div className="text-3xl font-bold text-white">{stats.total_hosts || hosts.length || 0}</div>
                    <div className="text-gray-400">Analyzed systems</div>
                  </div>
                  <div className="bg-black/30 rounded-xl p-6 border border-gray-700/50 text-center">
                    <Target className="w-10 h-10 text-orange-400 mx-auto mb-3" />
                    <div className="text-3xl font-bold text-white">{scans.length}</div>
                    <div className="text-gray-400">Tests performed</div>
                  </div>
                  <div className="bg-black/30 rounded-xl p-6 border border-gray-700/50 text-center">
                    <Bug className="w-10 h-10 text-red-400 mx-auto mb-3" />
                    <div className="text-3xl font-bold text-white">{vulnerabilities.length}</div>
                    <div className="text-gray-400">Points de controle</div>
                  </div>
                </div>

                <div className="bg-black/30 rounded-xl p-6 border border-gray-700/50">
                  <h4 className="text-white font-semibold mb-4">Glossaire</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3 p-3 bg-red-500/5 rounded-lg border border-red-500/20">
                      <div className="w-4 h-4 rounded-full bg-red-500 mt-0.5" />
                      <div>
                        <span className="text-red-400 font-medium">Critique</span>
                        <p className="text-gray-400 text-sm mt-1">Probleme tres grave pouvant permettre une prise de controle. Action immediate requise.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-orange-500/5 rounded-lg border border-orange-500/20">
                      <div className="w-4 h-4 rounded-full bg-orange-500 mt-0.5" />
                      <div>
                        <span className="text-orange-400 font-medium">Eleve</span>
                        <p className="text-gray-400 text-sm mt-1">Probleme important pouvant compromettre la securite. A corriger rapidement.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-yellow-500/5 rounded-lg border border-yellow-500/20">
                      <div className="w-4 h-4 rounded-full bg-yellow-500 mt-0.5" />
                      <div>
                        <span className="text-yellow-400 font-medium">Moyen</span>
                        <p className="text-gray-400 text-sm mt-1">Probleme a surveiller qui pourrait etre exploite. A planifier.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
                      <div className="w-4 h-4 rounded-full bg-blue-500 mt-0.5" />
                      <div>
                        <span className="text-blue-400 font-medium">Faible</span>
                        <p className="text-gray-400 text-sm mt-1">Point d'amelioration recommande pour renforcer votre securite.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Workflow d'attaque style n8n */}
                <AttackWorkflowN8N vulnerabilities={vulnerabilities} />
              </div>
            </AccordionSection>
          </section>

          {/* Section 5: Details Techniques */}
          <section className="mb-8">
            <AccordionSection
              title="Details Techniques (pour votre equipe IT)"
              icon={Terminal}
              iconColor="text-gray-400"
              iconBg="bg-gray-700/50"
            >
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-red-500/10 rounded-xl border border-red-500/30">
                    <div className="text-3xl font-bold text-red-400">{criticalCount}</div>
                    <div className="text-sm text-gray-400">Critiques</div>
                  </div>
                  <div className="text-center p-4 bg-orange-500/10 rounded-xl border border-orange-500/30">
                    <div className="text-3xl font-bold text-orange-400">{highCount}</div>
                    <div className="text-sm text-gray-400">Elevees</div>
                  </div>
                  <div className="text-center p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/30">
                    <div className="text-3xl font-bold text-yellow-400">{mediumCount}</div>
                    <div className="text-sm text-gray-400">Moyennes</div>
                  </div>
                  <div className="text-center p-4 bg-blue-500/10 rounded-xl border border-blue-500/30">
                    <div className="text-3xl font-bold text-blue-400">{stats.low_count || 0}</div>
                    <div className="text-sm text-gray-400">Faibles</div>
                  </div>
                </div>

                {vulnerabilities.length > 0 && (
                  <div>
                    <h4 className="text-white font-semibold mb-4">Liste des vulnerabilites</h4>
                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                      {vulnerabilities.map((vuln, idx) => (
                        <div key={idx} className="bg-black/30 rounded-lg p-4 border border-gray-700/50 hover:border-gray-600/50 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="text-white font-medium truncate">{vuln.title || vuln.name}</div>
                              <div className="text-gray-500 text-sm mt-1">
                                {vuln.host || vuln.ip_address}:{vuln.port}
                                {vuln.cve && <span className="text-cyan-400 ml-2 font-mono text-xs">{vuln.cve}</span>}
                              </div>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              vuln.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                              vuln.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                              vuln.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {vuln.severity?.toUpperCase() || 'N/A'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </AccordionSection>
          </section>

          {/* Section 6: Cartographie */}
          <section className="mb-8">
            <CartographySection workspaceId={ws?.id} hosts={hosts} />
          </section>

          {/* Besoin d'aide? */}
          <section className="mb-8">
            <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Besoin d'aide?</h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                Notre equipe est disponible pour vous accompagner dans la mise en oeuvre des recommandations
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                {(ws?.contact_email || ws?.owner_email) && (
                  <a
                    href={`mailto:${ws.contact_email || ws.owner_email}`}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-cyan-500/25"
                  >
                    <Mail className="w-5 h-5" />
                    {ws.contact_email || ws.owner_email}
                  </a>
                )}
                {(ws?.contact_phone || ws?.owner_phone) && (
                  <a
                    href={`tel:${ws.contact_phone || ws.owner_phone}`}
                    className="flex items-center gap-2 px-6 py-3 bg-black/50 border border-gray-700 text-white font-semibold rounded-xl hover:border-cyan-500/50 transition-all"
                  >
                    <Phone className="w-5 h-5" />
                    {ws.contact_phone || ws.owner_phone}
                  </a>
                )}
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="border-t border-gray-800 px-4 py-8 bg-black/50 backdrop-blur-md">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <span className="text-white font-bold text-lg">SYLink Pentest</span>
            </div>
            <p className="text-gray-500 text-sm">
              Confidential report - Secure and encrypted access
            </p>
          </div>
        </footer>
      </div>
    );
  }

  return null;
};

// ==========================================
// FONCTIONS UTILITAIRES
// ==========================================

function getClientFriendlyTitle(vuln) {
  const title = (vuln.title || vuln.name || '').toLowerCase();

  if (title.includes('sql injection') || title.includes('sqli')) {
    return 'Flaw allowing access to your database';
  }
  if (title.includes('xss') || title.includes('cross-site scripting')) {
    return 'Faille permettant des attaques sur vos utilisateurs';
  }
  if (title.includes('rce') || title.includes('remote code')) {
    return 'Flaw allowing control over your server';
  }
  if (title.includes('ssl') || title.includes('tls') || title.includes('certificate')) {
    return 'Communications security issue';
  }
  if (title.includes('authentication') || title.includes('login') || title.includes('password')) {
    return 'Weakness in access protection';
  }
  if (title.includes('outdated') || title.includes('version') || title.includes('update')) {
    return 'Software requiring a security update';
  }
  if (title.includes('ssh') || title.includes('ftp') || title.includes('telnet')) {
    return 'Remote access service to secure';
  }
  if (title.includes('smb') || title.includes('file share')) {
    return 'File share to secure';
  }

  return vuln.title || vuln.name || 'Detected vulnerability';
}

function getClientFriendlyDescription(vuln) {
  const severity = vuln.severity?.toLowerCase();

  if (severity === 'critical') {
    return 'This flaw represents a major risk to your organization security and requires immediate remediation.';
  }
  if (severity === 'high') {
    return 'This vulnerability could be exploited by attackers and must be remediated quickly.';
  }
  if (severity === 'medium') {
    return 'This security issue should be addressed to strengthen the protection of your systems.';
  }
  return 'This improvement is recommended to optimize your security level.';
}

function getClientFriendlyImpact(vuln) {
  // Use the actual vulnerability description if available
  if (vuln.description && vuln.description.length > 20) {
    // Simplifier la description technique pour le client
    let desc = vuln.description;
    // Limit to 300 characters and end cleanly
    if (desc.length > 300) {
      desc = desc.substring(0, 300);
      const lastSpace = desc.lastIndexOf(' ');
      if (lastSpace > 200) {
        desc = desc.substring(0, lastSpace) + '...';
      } else {
        desc += '...';
      }
    }
    return desc;
  }

  // Fallback based on title and vulnerability type
  const title = (vuln.title || vuln.name || '').toLowerCase();
  const cve = (vuln.cve_id || vuln.cve || '').toUpperCase();

  // Specific impacts by vulnerability type
  if (title.includes('sql injection') || title.includes('sqli')) {
    return `This SQL injection flaw (${cve || 'detected'}) allows an attacker to execute arbitrary commands on your database. This could result in theft, modification or deletion of sensitive data, including customer information and passwords.`;
  }
  if (title.includes('rce') || title.includes('remote code') || title.includes('command injection')) {
    return `This remote code execution vulnerability (${cve || 'detected'}) allows an attacker to take full control of your server. They could install malware, steal all your data, or use your infrastructure to attack other systems.`;
  }
  if (title.includes('xss') || title.includes('cross-site scripting')) {
    return `This XSS flaw (${cve || 'detected'}) allows an attacker to inject malicious code into your web pages. This could enable session theft, redirection to fraudulent sites, or theft of login credentials.`;
  }
  if (title.includes('ssl') || title.includes('tls') || title.includes('certificate')) {
    return `This SSL/TLS certificate vulnerability (${cve || 'detected'}) compromises the encryption of your communications. Sensitive data (passwords, banking information) could be intercepted by attackers on the network.`;
  }
  if (title.includes('authentication') || title.includes('bypass') || title.includes('auth')) {
    return `This authentication flaw (${cve || 'detected'}) allows bypassing security mechanisms. An attacker could access user or administrator accounts without knowing their credentials.`;
  }
  if (title.includes('buffer overflow') || title.includes('memory')) {
    return `This buffer overflow vulnerability (${cve || 'detected'}) may allow an attacker to execute arbitrary code or crash the system, causing service unavailability.`;
  }
  if (title.includes('path traversal') || title.includes('directory') || title.includes('lfi') || title.includes('file inclusion')) {
    return `This path traversal flaw (${cve || 'detected'}) allows access to sensitive system files. An attacker could read configuration files, passwords, or other confidential data.`;
  }
  if (title.includes('outdated') || title.includes('version') || title.includes('obsolete')) {
    return `This outdated software (${cve || 'identified'}) contains known and documented security flaws. Automated attack tools specifically target these vulnerable versions.`;
  }
  if (title.includes('information disclosure') || title.includes('info leak')) {
    return `This information leak (${cve || 'detected'}) exposes sensitive technical data. This information could help an attacker plan and execute more sophisticated attacks.`;
  }
  if (title.includes('denial') || title.includes('dos') || title.includes('ddos')) {
    return `This denial-of-service vulnerability (${cve || 'detected'}) can render your systems unavailable. An attacker could paralyze your services and impact your business.`;
  }
  if (title.includes('csrf') || title.includes('cross-site request')) {
    return `This CSRF flaw (${cve || 'detected'}) allows forcing your users to perform unwanted actions. An attacker could modify account settings or perform transactions without their knowledge.`;
  }

  // If CVE is present, mention it is a known vulnerability
  if (cve) {
    return `This vulnerability referenced ${cve} is a documented security flaw that could be exploited to compromise the confidentiality, integrity, or availability of your systems and data.`;
  }

  return 'This vulnerability represents a security risk that could be exploited to compromise your systems or access your sensitive data.';
}

function getClientFriendlySolution(vuln) {
  // Utiliser la solution fournie par le scan si disponible
  if (vuln.solution && vuln.solution.length > 20) {
    return vuln.solution;
  }

  const title = (vuln.title || vuln.name || '').toLowerCase();
  const cve = (vuln.cve_id || vuln.cve || '').toUpperCase();
  const severity = (vuln.severity || '').toLowerCase();

  // Specific solutions by vulnerability type
  if (title.includes('sql injection') || title.includes('sqli')) {
    return `PRIORITY ACTION: 1) Implement prepared statements in your code. 2) Use a secure ORM (Object-Relational Mapping). 3) Validate and escape all user inputs. 4) Apply the principle of least privilege to database accounts.`;
  }
  if (title.includes('rce') || title.includes('remote code') || title.includes('command injection')) {
    return `CRITICAL ACTION: 1) Immediately apply the available security patch. 2) Update to the latest version of the affected software. 3) Isolate the affected system from the network if possible. 4) Check logs to detect any prior exploitation.`;
  }
  if (title.includes('xss') || title.includes('cross-site scripting')) {
    return `REQUIRED ACTION: 1) Encode all HTML output (use escaping functions). 2) Implement a strict CSP (Content Security Policy). 3) Validate and filter user inputs server-side. 4) Use frameworks that automatically escape data.`;
  }
  if (title.includes('ssl') || title.includes('tls') || title.includes('certificate')) {
    return `REQUIRED ACTION: 1) Renew the SSL/TLS certificate with a recognized certificate authority. 2) Configure TLS 1.2 minimum (disable TLS 1.0/1.1). 3) Enable HSTS (HTTP Strict Transport Security). 4) Use modern and secure cipher suites.`;
  }
  if (title.includes('authentication') || title.includes('bypass') || title.includes('auth')) {
    return `PRIORITY ACTION: 1) Implement multi-factor authentication (MFA/2FA). 2) Implement account lockout after multiple failures. 3) Strengthen the password policy. 4) Audit existing authentication mechanisms.`;
  }
  if (title.includes('buffer overflow') || title.includes('memory')) {
    return `CRITICAL ACTION: 1) Apply the patch or update provided by the vendor. 2) If unavailable, consider disabling the affected feature. 3) Implement ASLR and DEP if not already in place. 4) Monitor logs for any suspicious activity.`;
  }
  if (title.includes('path traversal') || title.includes('directory') || title.includes('lfi') || title.includes('file inclusion')) {
    return `REQUIRED ACTION: 1) Validate and normalize all file paths server-side. 2) Use an allowlist of accessible files. 3) Apply restrictive permissions to directories. 4) Disable dynamic file inclusions if possible.`;
  }
  if (title.includes('outdated') || title.includes('version') || title.includes('obsolete')) {
    return `RECOMMENDED ACTION: 1) Plan an update to the latest stable version of the software. 2) Review release notes for security patches. 3) Test the update in a pre-production environment. 4) Implement an update monitoring process.`;
  }
  if (title.includes('information disclosure') || title.includes('info leak')) {
    return `REQUIRED ACTION: 1) Configure HTTP security headers (X-Content-Type-Options, etc.). 2) Disable detailed error messages in production. 3) Remove sensitive comments and metadata. 4) Audit API responses for excessive data.`;
  }
  if (title.includes('denial') || title.includes('dos') || title.includes('ddos')) {
    return `RECOMMENDED ACTION: 1) Implement rate limiting. 2) Configure appropriate firewall rules. 3) Use a DDoS protection service (CDN with protection). 4) Monitor performance metrics to detect anomalies.`;
  }
  if (title.includes('csrf') || title.includes('cross-site request')) {
    return `REQUIRED ACTION: 1) Implement CSRF tokens on all forms. 2) Verify the Origin/Referer header for sensitive requests. 3) Use the SameSite attribute on cookies. 4) Require re-authentication for critical actions.`;
  }
  if (title.includes('password') || title.includes('weak') || title.includes('default')) {
    return `PRIORITY ACTION: 1) Immediately change default passwords. 2) Implement a password complexity policy. 3) Use a password manager. 4) Enable multi-factor authentication.`;
  }

  // Solution based on severity
  if (severity === 'critical') {
    return `ACTION IMMEDIATE REQUISE: Cette vulnerabilite critique ${cve ? `(${cve})` : ''} necessite une intervention urgente. Contactez votre equipe securite pour appliquer le correctif dans les 24-48h et verifiez qu'aucune exploitation n'a eu lieu.`;
  }
  if (severity === 'high') {
    return `ACTION PRIORITAIRE: Cette vulnerabilite haute ${cve ? `(${cve})` : ''} doit etre corrigee dans les 7 jours. Planifiez l'application du correctif avec votre equipe technique et surveillez les logs pour toute activite suspecte.`;
  }
  if (severity === 'medium') {
    return `ACTION PLANIFIEE: Cette vulnerabilite moyenne ${cve ? `(${cve})` : ''} doit etre traitee dans les 30 jours. Integrez la correction dans votre prochain cycle de maintenance et documentez le plan de remediation.`;
  }

  return `RECOMMANDATION: Consultez la documentation technique de cette vulnerabilite ${cve ? `(${cve})` : ''} et appliquez les correctifs recommandes. Contactez votre equipe informatique ou votre prestataire de securite pour assistance.`;
}

export default SharePage;
